import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { STATUS } from '../../../../constants/commonConstants';
import useAnalytics from '../../../../hooks/useAnalytics';
import { convertFormattedNumberToUnformatted } from '@app/components/pages/checkout/utils';

const ProductAnalytics = ({
  current,
  fitmentCheckStatus,
  isCatCorp,
  isCurrentLoaded: didProductInformationResolved,
  priceCallStatus,
  serialNumber,
  getFitmentCheck
}) => {
  const [didGAEventFired, setDidGAEventFired] = useState(false);

  const currencyCode = useSelector(s => s.dealerCustomer?.currency) ?? '';
  const name = useSelector(s => s.products.byId?.[current]?.name);
  const partNumber = useSelector(s => s.products.byId?.[current]?.partNumber);
  const brand = useSelector(s => s.products.byId?.[current]?.manufacturer);
  const availability = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.availability
  );
  const sourcingDetails = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.sourcingDetails
  );
  const unformattedUnitPrice = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.unformattedUnitPrice
  );
  const discountedAmount = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.discountedAmount
  );
  const totalPrice = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.unformattedTotalPrice
  );
  const itemCatEntryId = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.itemCatEntryId
  );
  const productWeight = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.weight
  );
  const quantity = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.packageQty
  );
  const category = useSelector(s => s.products.byId?.[current]?.categoryPath);
  const relatedModel = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const status = useSelector(s => s.products.byId?.[current]?.status);

  const shouldHaveFitmentInformation = !!serialNumber;
  const didFitmentResolved =
    fitmentCheckStatus === STATUS.RESOLVED ||
    fitmentCheckStatus === STATUS.REJECTED;

  const shouldHavePrice = !isCatCorp;
  const didPriceCallResolve =
    priceCallStatus === STATUS.RESOLVED || priceCallStatus === STATUS.REJECTED;
  const { fireProductDetailReadyEvent } = useAnalytics();

  useEffect(() => {
    if (
      !didGAEventFired &&
      didProductInformationResolved &&
      (!shouldHavePrice || didPriceCallResolve) &&
      (!shouldHaveFitmentInformation || didFitmentResolved)
    ) {
      const list = '';

      const product = {
        availability,
        sourcingDetails,
        brand: brand || 'Cat',
        category: category?.split('/'),
        checkFitment: shouldHaveFitmentInformation
          ? getFitmentCheck(partNumber)
          : '',
        currencyCode,
        list,
        name,
        quantity: quantity ?? '',
        partNumber,
        position: 1,
        price: convertFormattedNumberToUnformatted(unformattedUnitPrice || ''),
        productWeight,
        discountedAmount: convertFormattedNumberToUnformatted(
          discountedAmount || ''
        ),
        itemCatEntryId: itemCatEntryId,
        relatedModel
      };
      const gaProducts = {
        product: product,
        totalPrice,
        status
      };
      fireProductDetailReadyEvent(gaProducts);
      setDidGAEventFired(true);
    }
  }, [
    availability,
    sourcingDetails,
    brand,
    category,
    currencyCode,
    didFitmentResolved,
    didGAEventFired,
    didPriceCallResolve,
    didProductInformationResolved,
    fireProductDetailReadyEvent,
    getFitmentCheck,
    name,
    partNumber,
    productWeight,
    quantity,
    relatedModel,
    shouldHaveFitmentInformation,
    shouldHavePrice,
    unformattedUnitPrice
  ]);
  return null;
};

export default ProductAnalytics;

ProductAnalytics.propTypes = {
  current: PropTypes.string,
  fitmentCheckStatus: PropTypes.string,
  isCatCorp: PropTypes.bool,
  isCurrentLoaded: PropTypes.bool,
  priceCallStatus: PropTypes.string,
  serialNumber: PropTypes.string,
  getFitmentCheck: PropTypes.func
};
